/**
 * Password requirement rules — SINGLE SOURCE OF TRUTH for the frontend.
 *
 * The returned object's keys are a contract with
 * `PasswordRequirementsChecklist.jsx`, which renders one row per key.
 * Callers keep their own user-facing copy for anything beyond the three
 * shape rules below (e.g. "you must enter a password"), because that
 * wording is deliberately different per surface.
 */

export const PASSWORD_SYMBOL_REGEX = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/;

/**
 * @param {string} password
 * @param {string} confirm Confirmation field; omit when a surface has none.
 * @returns {{minLength: boolean, hasNumber: boolean, hasSymbol: boolean, passwordsMatch: boolean}}
 */
export const evaluatePasswordRequirements = (password = "", confirm = "") => ({
  minLength: password.length >= 8,
  hasNumber: /\d/.test(password),
  hasSymbol: PASSWORD_SYMBOL_REGEX.test(password),
  passwordsMatch:
    password === confirm && password.length > 0 && confirm.length > 0,
});

/**
 * First unmet shape rule, as a user-facing message. Returns "" when the
 * password satisfies all three shape rules (match is the caller's business).
 *
 * @param {ReturnType<typeof evaluatePasswordRequirements>} requirements
 * @returns {string}
 */
export const firstPasswordRequirementMessage = (requirements) => {
  if (!requirements?.minLength) {
    return "Password must be at least 8 characters long";
  }
  if (!requirements.hasNumber) {
    return "Password must contain at least one number";
  }
  if (!requirements.hasSymbol) {
    return "Password must contain at least one symbol";
  }
  return "";
};
